import AsyncStorage from '@react-native-async-storage/async-storage';
import { CommonActions } from "@react-navigation/native";
import { AUTH_API_URL } from '../config/api';
import { logout } from '../services/authApi';

const API_BASE_URL = AUTH_API_URL;

// Standard response format handler
const handleApiResponse = async (response, responseData = null) => {
  // If responseData is provided, use it directly (already parsed JSON)
  if (responseData) {
    // Handle different success response formats
    if ((responseData.status === 'success' && responseData.code === 200) ||
        responseData.success === true) {
      return responseData;
    }
    // Paygift update-voucher-url: pending is not an error for callers
    if (responseData.success === false && responseData.pending === true) {
      return responseData;
    }
    // Report APIs: voucher already ready (callback race) — not a hard failure
    if (responseData.success === false && responseData.resolved === true) {
      return responseData;
    }
    const err = new Error(responseData.message || 'API request failed');
    err.code = response.status;
    if (responseData.error_code) {
      err.apiErrorCode = responseData.error_code;
    }
    if (responseData.data) {
      err.data = responseData.data;
    }
    throw err;
  }
  
  // Handle network errors or non-JSON responses
  if (!response.ok) {
    const responseError = new Error();

    if (response.status === 401) {

      responseError.message = 'Email atau password salah';
      
      // check if response has body
      const responseType = response.headers.get('content-type');
      if( !!responseType && responseType.includes('application/json') ) {
        const responseBody = await response.json();
        responseError.message = responseBody?.message || responseError.message;
      }

    } else if (response.status === 422) {
      responseError.message = 'Data yang dimasukkan tidak valid';
    } else if (response.status >= 500) {
      responseError.message = 'Server sedang bermasalah. Silakan coba lagi nanti.';
    } else {
      responseError.message = `HTTP Error: ${response.status}`;
    }
    responseError.code = response.status;
    throw responseError;
  }

  let data;
  try {
    data = await response.json();
  } catch (error) {
    throw new Error('Server response tidak valid');
  }
  
  // Handle different success response formats
  if ((data.status === 'success' && data.code === 200) ||
      data.success === true) {
    return data;
  }
  // Paygift update-voucher-url: pending is not an error for callers
  if (data.success === false && data.pending === true) {
    return data;
  }
  // Report APIs: voucher already ready (callback race) — not a hard failure
  if (data.success === false && data.resolved === true) {
    return data;
  }
  const err = new Error(data.message || 'API request failed');
  if (data.error_code) {
    err.apiErrorCode = data.error_code;
  }
  if (data.data) {
    err.data = data.data;
  }
  throw err;
};

// Helper function to get stored user data
export const getUserData = async () => {
  try {
    const userData = await AsyncStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    return null;
  }
};

// Helper function to check if user is authenticated
export const isAuthenticated = async () => {
  try {
    const token = await AsyncStorage.getItem('userToken');
    const sessionId = await AsyncStorage.getItem('session_id');
    
    // User is authenticated if they have both userToken and session_id
    // This ensures API calls will work properly
    console.log('isAuthenticated check:', {
      hasToken: !!token,
      hasSessionId: !!sessionId,
      tokenValue: token,
      sessionIdValue: sessionId
    });
    
    return !!(token && sessionId);
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

// Helper function to clear authentication data
export const clearAuthData = async () => {
  try {
    // Clear all user-related data from AsyncStorage (incl. auth_token, externalApiResponse for SSO user-switch)
    await AsyncStorage.multiRemove([
      'userToken',
      'userData',
      'session_id',
      'remember_token',
      'auth_token',
      'externalApiResponse',
    ]);
    console.log('All user data cleared successfully');
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
};

// Fetch fresh user profile data from backend using session-based authentication
export const fetchUserProfile = async () => {
  try {
    // Get session data from AsyncStorage
    const sessionId = await AsyncStorage.getItem('session_id');
    const rememberToken = await AsyncStorage.getItem('remember_token');
    
    console.log('fetchUserProfile session check');
    // console.log('fetchUserProfile session check:', {
    //   sessionId: sessionId ? 'Present' : 'Missing',
    //   rememberToken: rememberToken ? 'Present' : 'Missing',
    //   sessionIdValue: sessionId
    // });
    
    if (!sessionId) {
      throw new Error('No session ID available');
    }
    
    // Prepare headers with session authentication
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': sessionId,
    };
    
    if (rememberToken) {
      headers['X-Remember-Token'] = rememberToken;
    }
    
    console.log('Making fetchUserProfile request with headers:', {
      hasSessionId: !!headers['X-Session-ID'],
      hasRememberToken: !!headers['X-Remember-Token']
    });
    
    const response = await fetch(`${API_BASE_URL}/getUserProfile`, {
      method: 'GET',
      headers,
    });
    
    const result = await handleApiResponse(response);
    
    // Update local storage with fresh data
    if (result.data) {
      await AsyncStorage.setItem('userData', JSON.stringify(result.data));
      console.log('User profile data updated in AsyncStorage');
    }
    
    // Update auth_token in localStorage if included in response
    if (result.data && result.data.auth_token) {
      await AsyncStorage.setItem('auth_token', result.data.auth_token);
      console.log('Updated auth_token in localStorage');
    } else {
      console.log('No auth_token found in response');
    }
    
    return result;
  } catch (error) {
    // If error is 401 Unauthorized, that means session is expired
    // Force logout here
    if( error.code && error.code === 401 ) {
      doLogout();
      return;
    }

    // If API call fails, return cached data
    console.log('Failed to fetch fresh user data, using cached data:', error.message);
    const cachedData = await getUserData();
    if (cachedData) {
      return { data: cachedData };
    }
    throw error;
  }
};

// Handle logout
export const doLogout = async () => {
  try {
    console.log("Starting logout process...");
    const userData = await AsyncStorage.getItem('userData');

    // Call the real logout API with userId
    const userId = userData?.user_id;
    await logout(userId);
    console.log("Logout successful");

    // Clear all AsyncStorage data (app storage)
    console.log("Clearing AsyncStorage...");

    // await AsyncStorage.clear();
    // for phone only auth, we need to keep stored pasword
    const storageKeys = await AsyncStorage.getAllKeys();
    if( storageKeys.includes("zclub-credential") ) {
      storageKeys.splice(storageKeys.indexOf("zclub-credential"), 1);
    }
    if( storageKeys.includes("pending_qr_code") ) {
      storageKeys.splice(storageKeys.indexOf("pending_qr_code"), 1);
    }
    if( storageKeys.includes("last_pending_qr_timestamp") ) {
      storageKeys.splice(storageKeys.indexOf("last_pending_qr_timestamp"), 1);
    }
    await AsyncStorage.multiRemove(storageKeys);

    console.log("AsyncStorage cleared successfully");

    // Force navigation to Splash screen with complete reset
    // This effectively reloads the app by resetting the entire navigation stack
    CommonActions.reset({
      index: 0,
      routes: [{ name: "Splash" }],
    });
    
    // Force complete page reload to ensure fresh state
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload(true); // force reload from server
      }
    }, 100);
  } catch (error) { 
    console.error("Logout error:", error);
    if (error.code && error.code === 401) {
      window.location.reload();
    }
  }
};

// Export handleApiResponse for use in other services
export { handleApiResponse };